import type { Ref } from 'vue';
import type { DateValue } from 'reka-ui';
import { CalendarDate, parseDate } from '@internationalized/date';
import type { EventCalendarDTO } from '~/shared/api/client/events/events.dto';
import {
  CLIENT_API_EVENT_CALENDAR_URL,
  CLIENT_API_EVENT_CALENDAR_KEY,
} from '~/shared/api/client/events/events.const';
import { useCustomFetch } from '~/shared/lib/useCustomFetch';
import type { EventType, EventPosition, EventDateInfo } from './const';

export function useEventCalendar() {
  const now = new Date();
  const placeholder = ref(
    new CalendarDate(now.getFullYear(), now.getMonth() + 1, 1),
  ) as Ref<DateValue>;

  const currentMonth = computed(() => placeholder.value.month);
  const currentYear = computed(() => placeholder.value.year);

  function prevMonth() {
    placeholder.value = placeholder.value.subtract({ months: 1 });
  }

  function nextMonth() {
    placeholder.value = placeholder.value.add({ months: 1 });
  }

  function prevYear() {
    placeholder.value = placeholder.value.subtract({ years: 1 });
  }

  function nextYear() {
    placeholder.value = placeholder.value.add({ years: 1 });
  }

  const { data: calendarData } = useCustomFetch<EventCalendarDTO[]>(
    CLIENT_API_EVENT_CALENDAR_URL,
    {
      key: CLIENT_API_EVENT_CALENDAR_KEY,
      params: {
        year: currentYear,
        month: currentMonth,
      },
      watch: [currentYear, currentMonth],
    },
  );

  const events = computed(() => calendarData.value ?? []);

  const eventDateMap = computed(() => {
    const map = new Map<string, EventDateInfo>();

    for (const event of events.value) {
      // No exact days to mark: a month-only event is not drawn by day
      if (event.month_only) continue;

      const type: EventType = event.partner_event ? 'partner' : 'cre';
      const startDay = parseDate(event.start_time.slice(0, 10));
      const endDay = parseDate(event.end_time.slice(0, 10));
      const isSingle = startDay.compare(endDay) === 0;

      let current = startDay;

      while (current.compare(endDay) <= 0) {
        const key = current.toString();
        const existing = map.get(key);

        let position: EventPosition;
        if (isSingle) {
          position = 'single';
        } else if (current.compare(startDay) === 0) {
          position = 'start';
        } else if (current.compare(endDay) === 0) {
          position = 'end';
        } else {
          position = 'middle';
        }

        if (!existing || type === 'cre') {
          map.set(key, { type, position, name: event.name, id: event.id });
        }
        current = current.add({ days: 1 });
      }
    }

    return map;
  });

  function getEventInfo(day: DateValue): EventDateInfo | null {
    return eventDateMap.value.get(day.toString()) ?? null;
  }

  return {
    placeholder,
    currentMonth,
    currentYear,
    prevMonth,
    nextMonth,
    prevYear,
    nextYear,
    getEventInfo,
  };
}
